/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const toISO   = d => d.toISOString().split('T')[0];
const fromISO = s => new Date(s + 'T12:00:00');
const fmtDate = d => d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
const fmtNum  = n => Number(n || 0).toLocaleString('fr-FR');

// ── Calcul prix dynamique selon pricing_rules ────────────────────────────────
function calculateDynamicPrice(priceNight, dateStart, dateEnd, rules = []) {
  const start  = new Date(dateStart + 'T12:00:00');
  const end    = new Date(dateEnd   + 'T12:00:00');
  const nights = Math.round((end - start) / 86400000);
  if (nights <= 0) return { total: 0, perNight: priceNight, nights: 0, breakdown: [] };

  const nightDates = [];
  for (let i = 0; i < nights; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    nightDates.push(d);
  }

  let total = 0;
  const breakdown = [];

  nightDates.forEach(date => {
    const dateStr = date.toISOString().split('T')[0];
    const dow     = date.getDay(); // 0=dim, 6=sam
    const isWE    = dow === 5 || dow === 6;
    const month   = date.getMonth() + 1;
    const day     = date.getDate();
    let price     = priceNight;
    let applied   = [];

    // Priorité : saison > vacances > weekend
    const priority = { season: 1, school_holidays: 2, weekend: 3, long_stay: 4 };
    const sorted   = [...rules].sort((a, b) => (priority[a.rule_type] || 5) - (priority[b.rule_type] || 5));

    sorted.forEach(rule => {
      if (rule.rule_type === 'long_stay') return; // appliqué sur le total après
      let applies = false;
      if (rule.rule_type === 'weekend' && isWE) {
        applies = true;
      } else if ((rule.rule_type === 'season' || rule.rule_type === 'school_holidays') && rule.date_start && rule.date_end) {
        if (rule.rule_type === 'season') {
          const md  = `${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const smd = rule.date_start.slice(5);
          const emd = rule.date_end.slice(5);
          applies   = smd <= emd ? (md >= smd && md <= emd) : (md >= smd || md <= emd);
        } else {
          applies = dateStr >= rule.date_start && dateStr <= rule.date_end;
        }
      }
      if (applies) {
        const adj = rule.adjustment_type === 'percent'
          ? price * (rule.adjustment_value / 100)
          : rule.adjustment_value;
        price += adj;
        applied.push(rule.label || rule.rule_type);
      }
    });

    price = Math.max(price, 1);
    total += price;
    breakdown.push({ date: dateStr, price: Math.round(price), applied });
  });

  // Long stay : réduction sur le total
  const ls = rules.find(r => r.rule_type === 'long_stay');
  if (ls && nights >= (ls.min_nights || 7)) {
    const disc = ls.adjustment_type === 'percent'
      ? total * (Math.abs(ls.adjustment_value) / 100)
      : Math.abs(ls.adjustment_value) * nights;
    total -= disc;
  }

  return {
    total:     Math.round(total),
    perNight:  Math.round(total / nights),
    nights,
    breakdown,
  };
}

export default function BookingPage({ listing, onNavigate }) {

  const todayD = new Date(); todayD.setHours(12,0,0,0);
  const defIn  = new Date(todayD); defIn.setDate(todayD.getDate() + 7);
  const defOut = new Date(defIn);  defOut.setDate(defIn.getDate() + 7);

  const l = listing || { id: null, title: 'Villa provençale', location: "Nice · Côte d'Azur", price_night: 178, rating: 4.93, reviews: 0, emoji: '🏡', bg: '#E1F5EE' };

  const [guests,          setGuests]          = useState(l.guests || 2);
  const [status,          setStatus]          = useState('form');
  const [isMobile,        setIsMobile]        = useState(window.innerWidth < 768);
  const [user,            setUser]            = useState(null);
  const [loading,         setLoading]         = useState(false);
  const [bookingRef,      setBookingRef]      = useState(null);
  const [error,           setError]           = useState(null);
  const [dateIn,          setDateIn]          = useState(l.dateIn  || toISO(defIn));
  const [dateOut,         setDateOut]         = useState(l.dateOut || toISO(defOut));
  const [stripeInstance,  setStripeInstance]  = useState(null);
  const [cardElement,     setCardElement]     = useState(null);
  const [clientSecret,    setClientSecret]    = useState(null);
  const [depositIntentId, setDepositIntentId] = useState(null);
  const [stripeError,     setStripeError]     = useState(null);
  const [cardMounted,     setCardMounted]     = useState(false);
  const [bookedRanges,    setBookedRanges]    = useState([]);
  const [nextAvailable,   setNextAvailable]   = useState(null);
  const [pricingRules,    setPricingRules]    = useState([]);
  const [rulesLoaded,     setRulesLoaded]     = useState(false);

  // Resize
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  // Auth
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setUser(session?.user ?? null));
  }, []);

  // Charger les dates réservées
  useEffect(() => {
    if (!l.id) return;
    const today = new Date(); today.setHours(12,0,0,0);
    const todayISO = today.toISOString().split('T')[0];
    supabase
      .from('bookings')
      .select('date_start, date_end')
      .eq('listing_id', l.id)
      .in('status', ['pending', 'confirmed', 'checkin'])
      .gte('date_end', todayISO)
      .then(({ data }) => {
        if (!data?.length) return;
        setBookedRanges(data);
        const today2 = new Date(); today2.setHours(12,0,0,0);
        const sorted = [...data].sort((a, b) => a.date_start.localeCompare(b.date_start));
        let next = new Date(today2);
        next.setDate(next.getDate() + 1);
        let changed = true;
        while (changed) {
          changed = false;
          for (const r of sorted) {
            const start = new Date(r.date_start + 'T12:00:00');
            const end   = new Date(r.date_end   + 'T12:00:00');
            if (next >= start && next < end) {
              next = new Date(end);
              next.setDate(next.getDate() + 1);
              changed = true;
            }
          }
        }
        setNextAvailable(next);
      });
  }, [l.id]);

  // ── Charger les pricing_rules depuis Supabase ────────────────────────────
  useEffect(() => {
    if (!l.id) { setRulesLoaded(true); return; }
    supabase
      .from('pricing_rules')
      .select('*')
      .eq('listing_id', l.id)
      .then(({ data }) => {
        setPricingRules(data || []);
        setRulesLoaded(true);
      });
  }, [l.id]);

  // Charger Stripe.js
  useEffect(() => {
    const init = () => {
      const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (!key || !window.Stripe) return;
      const stripe   = window.Stripe(key);
      const elements = stripe.elements();
      const card     = elements.create('card', {
        style: { base: { fontSize: '15px', color: '#1A1A18', fontFamily: 'sans-serif', '::placeholder': { color: '#bbb' } }, invalid: { color: '#A32D2D' } },
        hidePostalCode: true,
      });
      card.on('change', e => setStripeError(e.error ? e.error.message : null));
      setStripeInstance(stripe);
      setCardElement(card);
    };
    if (window.Stripe) { init(); }
    else {
      const s = document.createElement('script');
      s.src = 'https://js.stripe.com/v3/';
      s.onload = init;
      document.head.appendChild(s);
    }
  }, []);

  // Monter le card element
  useEffect(() => {
    if (!cardElement || cardMounted) return;
    const tryMount = () => {
      const container = document.getElementById('stripe-card-element');
      if (container && !container.firstChild) {
        cardElement.mount('#stripe-card-element');
        setCardMounted(true);
      }
    };
    tryMount();
    const t1 = setTimeout(tryMount, 100);
    const t2 = setTimeout(tryMount, 300);
    const t3 = setTimeout(tryMount, 600);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [cardElement, cardMounted]);

  // ── Calcul prix avec règles dynamiques ─────────────────────────────────
  const base      = l.price_night || l.priceNight || l.price || 0;
  const minNights = l.min_nights || 1;

  const priceCalc = React.useMemo(() => {
    if (!rulesLoaded) return { total: base * 7, perNight: base, nights: 7, breakdown: [] };
    return calculateDynamicPrice(base, dateIn, dateOut, pricingRules);
  }, [dateIn, dateOut, base, pricingRules, rulesLoaded]);

  const nights       = Math.max(minNights, priceCalc.nights);
  const baseTotal    = priceCalc.total;
  const priceNight   = priceCalc.perNight; // prix moyen/nuit pour affichage
  const hostName     = l.host?.full_name?.split(' ')[0] || "l'hôte";
  const cleaningFee  = l.price_cleaning || 0;
  const havenlyFee   = Math.round(baseTotal * 0.08);
  const taxeSejour   = l.taxe_sejour ? Math.round(l.taxe_sejour * guests * nights) : 0;
  const total = baseTotal + cleaningFee + taxeSejour;
  const airbnbEst    = Math.round(baseTotal * 0.22);
  const depositAmt   = l.price_deposit || l.caution || 0;

  // Y a-t-il des règles actives ?
  const hasRules = pricingRules.length > 0;
  const longStayRule = pricingRules.find(r => r.rule_type === 'long_stay');
  const hasLongStayDiscount = longStayRule && nights >= (longStayRule.min_nights || 7);

  // Créer PaymentIntent après calcul de total
  useEffect(() => {
    if (!user || !total || clientSecret || !rulesLoaded) return;
    fetch('/api/create-payment-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        amount: total,
        amountForCommission: baseTotal + cleaningFee,
        depositAmount: depositAmt,
        bookingId:     `temp-${Date.now()}`,
        listingTitle:  l.title,
        listingId:     l.id || null,
      }),
    })
    .then(r => r.json())
    .then(data => {
      if (!data.error) {
        setClientSecret(data.clientSecret);
        setDepositIntentId(data.depositIntentId || null);
      }
    })
    .catch(() => {});
  }, [user, total, rulesLoaded]);

  // Reset clientSecret si les dates changent (montant a changé)
  useEffect(() => {
    setClientSecret(null);
  }, [dateIn, dateOut, guests]);

  const isDateBooked = (dateStr) => bookedRanges.some(r => dateStr >= r.date_start && dateStr < r.date_end);

  const handleDateIn = (v) => {
    if (isDateBooked(v)) {
      const range = bookedRanges.find(r => v >= r.date_start && v < r.date_end);
      if (range) {
        const nextFree = new Date(range.date_end + 'T12:00:00');
        nextFree.setDate(nextFree.getDate() + 1);
        v = toISO(nextFree);
      }
    }
    setDateIn(v);
    const minOut = fromISO(v);
    minOut.setDate(minOut.getDate() + minNights);
    if (dateOut < toISO(minOut)) setDateOut(toISO(minOut));
  };

  const handleDateOut = (v) => {
    const minOut = fromISO(dateIn);
    minOut.setDate(minOut.getDate() + minNights);
    setDateOut(v < toISO(minOut) ? toISO(minOut) : v);
  };

  const handleSubmit = async () => {
    if (!user) { onNavigate('auth'); return; }
    if (nights < minNights) {
      setError(`Séjour minimum : ${minNights} nuit${minNights > 1 ? 's' : ''}.`);
      return;
    }
    if (!stripeInstance || !cardElement || !clientSecret) {
      setError('Paiement non prêt. Rechargez la page.');
      return;
    }
    setLoading(true); setError(null);
    try {
      // Vérification disponibilité
      const { data: conflicts } = await supabase
        .from('bookings')
        .select('id')
        .eq('listing_id', l.id)
        .in('status', ['pending', 'confirmed', 'checkin'])
        .lt('date_start', dateOut)
        .gt('date_end', dateIn);

      if (conflicts && conflicts.length > 0) {
        setError("Ces dates ne sont plus disponibles. Veuillez choisir d'autres dates.");
        setLoading(false);
        return;
      }

      const { error: stripeErr, paymentIntent } = await stripeInstance.confirmCardPayment(
        clientSecret, { payment_method: { card: cardElement } }
      );
      if (stripeErr) throw new Error(stripeErr.message);

      const ref = `HAV-${Date.now().toString(36).toUpperCase()}`;
      const { data, error: err } = await supabase.from('bookings').insert({
        listing_id:               l.id || null,
        guest_id:                 user.id,
        guest_email:              user.email || '',
        host_id:                  l.host_id || null,
        date_start:               dateIn,
        date_end:                 dateOut,
        nights,
        guests,
        price_night:              base,         // prix de base unitaire
        price_night_effective:    priceNight,   // prix moyen/nuit avec règles
        price_cleaning:           cleaningFee,
        price_total:              total,
        commission_rate:          0.08,
        commission_amount:        havenlyFee,
        host_payout:              baseTotal + cleaningFee,
        pricing_rules_applied:    pricingRules.map(r => r.id), // règles appliquées
        status:                   'pending',
        payment_status:           'authorized',
        stripe_payment_intent_id: paymentIntent.id,
        stripe_deposit_intent_id: depositIntentId,
        access_code:              ref,
      }).select().single();
      if (err) throw err;

      // Email hôte
      if (l.host_id) {
        const { data: host } = await supabase.from('host_profiles').select('email, full_name').eq('id', l.host_id).single();
        if (host?.email) {
          await fetch('/api/send-email', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'new_booking',
              to:   host.email,
              data: {
                hostName:     host.full_name?.split(' ')[0] || 'Hôte',
                guestName:    user.user_metadata?.full_name?.split(' ')[0] || user.email?.split('@')[0] || 'Un voyageur',
                listingTitle: l.title,
                dates:        `${fmtDate(fromISO(dateIn))} → ${fmtDate(fromISO(dateOut))}`,
                amount:       fmtNum(total),
                hostNet:      fmtNum(baseTotal + cleaningFee),
              }
            })
          }).catch(() => {});
        }
      }

      setBookingRef(data?.access_code || ref);
      setStatus('pending');
    } catch (e) {
      setError(e.message || "Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  // Page confirmation
  if (status === 'pending') return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', paddingTop: 80 }}>
      <div style={{ background: 'var(--white)', border: '0.5px solid var(--border)', borderRadius: 20, padding: isMobile ? '1.5rem' : '2.5rem', maxWidth: 520, width: '100%', textAlign: 'center' }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#FDF0E6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C4784A" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        </div>
        <h2 style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 22 : 26, letterSpacing: '-0.5px', marginBottom: 10 }}>Demande envoyée !</h2>
        <p style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 20, lineHeight: 1.7 }}>
          Votre demande a été transmise à <strong>{hostName}</strong>. {hostName === "l'hôte" ? "L'hôte a" : 'Il/Elle a'} <strong>24h</strong> pour confirmer ou refuser.
        </p>
        <div style={{ background: 'var(--surface)', borderRadius: 12, padding: '14px 16px', marginBottom: 20, textAlign: 'left' }}>
          {[
            { label: 'Logement',  val: l.title },
            { label: 'Arrivée',   val: fmtDate(fromISO(dateIn)) },
            { label: 'Départ',    val: fmtDate(fromISO(dateOut)) },
            { label: 'Voyageurs', val: `${guests} voyageur${guests > 1 ? 's' : ''}` },
            { label: 'Total',     val: `${fmtNum(total)} €` },
            { label: 'Référence', val: bookingRef || '—' },
          ].map(row => (
            <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '5px 0', borderBottom: '0.5px solid var(--border)' }}>
              <span style={{ color: 'var(--ink-3)' }}>{row.label}</span>
              <span style={{ color: 'var(--ink)', maxWidth: '60%', textAlign: 'right' }}>{row.val}</span>
            </div>
          ))}
        </div>
        <div style={{ background: 'var(--green-light)', border: '1px solid var(--green)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, textAlign: 'left' }}>
          <p style={{ fontSize: 13, color: 'var(--green-mid)', lineHeight: 1.6, margin: 0 }}>
            <strong>Votre carte a été autorisée mais pas débitée.</strong> Le paiement n'est effectué qu'après confirmation de l'hôte.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => onNavigate('mybookings')} style={{ padding: '10px 24px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 24, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>Mes réservations →</button>
          <button onClick={() => onNavigate('home')} style={{ padding: '10px 24px', background: 'transparent', color: 'var(--ink-3)', border: '1px solid var(--border)', borderRadius: 24, fontSize: 14, cursor: 'pointer', fontFamily: 'var(--sans)' }}>Retour à l'accueil</button>
        </div>
      </div>
    </div>
  );

  // Bloc Stripe
  const stripeBlock = user ? (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', marginBottom: 8 }}>💳 Informations de paiement</div>
      <div id="stripe-card-element" style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', background: '#fff', minHeight: 44 }} />
      {stripeError && <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 6 }}>{stripeError}</div>}
      {depositAmt > 0 && (
        <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 8, background: 'var(--surface)', borderRadius: 8, padding: '8px 10px' }}>
          🔒 {fmtNum(depositAmt)} € — Protection Séjour (autorisation bancaire uniquement, libérée 48h après votre départ).
        </div>
      )}
      <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 6, textAlign: 'center' }}>
        Votre carte ne sera débitée qu'après confirmation de l'hôte
      </div>
    </div>
  ) : null;

  const bouton = (
    <>
      {error && <div style={{ background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#A32D2D', marginBottom: 10 }}>{error}</div>}
      <button onClick={handleSubmit} disabled={loading || !user || !rulesLoaded}
        style={{ width: '100%', padding: 13, background: user ? 'var(--green)' : '#aaa', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 500, cursor: user ? 'pointer' : 'default', fontFamily: 'var(--sans)', opacity: loading ? 0.7 : 1 }}>
        {!user ? 'Connectez-vous pour réserver' : loading ? 'Traitement...' : 'Envoyer ma demande →'}
      </button>
      <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--ink-4)', marginTop: 6 }}>
        🔒 Paiement sécurisé · Stripe · PCI-DSS · Débité après confirmation hôte
      </div>
    </>
  );

  const recapLignes = (fontSize = 13) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* Ligne prix de base si pas de règles */}
      {!hasRules && (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize, padding: '5px 0' }}>
    <span style={{ color: 'var(--ink-3)' }}>{fmtNum(base)} € × {nights} nuit{nights > 1 ? 's' : ''}</span>
    <span>{fmtNum(baseTotal)} €</span>
  </div>
)}

      {/* Détail nuit par nuit si règles actives */}
      {hasRules && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize, padding: '5px 0' }}>
            <span style={{ color: 'var(--ink-3)' }}>
              {fmtNum(base)} € × {nights} nuit{nights > 1 ? 's' : ''}
              {hasRules && <span style={{ fontSize: fontSize - 1, color: 'var(--green-mid)', marginLeft: 6 }}>+ tarifs dynamiques</span>}
            </span>
            <span>{fmtNum(baseTotal)} €</span>
          </div>
          {/* Aperçu des règles appliquées */}
          {priceCalc.breakdown.some(n => n.applied.length > 0) && (
            <details style={{ marginBottom: 4 }}>
              <summary style={{ fontSize: fontSize - 1, color: 'var(--ink-4)', cursor: 'pointer', paddingLeft: 8 }}>
                Voir le détail nuit par nuit
              </summary>
              <div style={{ paddingLeft: 8, marginTop: 4, maxHeight: 120, overflowY: 'auto' }}>
                {priceCalc.breakdown.map((n, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: fontSize - 2, padding: '2px 0', color: 'var(--ink-3)' }}>
                    <span>
                      {new Date(n.date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                      {n.applied.length > 0 && <span style={{ color: 'var(--green-mid)', marginLeft: 4 }}>({n.applied.join(', ')})</span>}
                    </span>
                    <span style={{ fontWeight: n.price !== base ? 500 : 400, color: n.price !== base ? 'var(--ink)' : 'var(--ink-3)' }}>{fmtNum(n.price)} €</span>
                  </div>
                ))}
              </div>
            </details>
          )}
          {hasLongStayDiscount && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: fontSize - 1, padding: '3px 0', color: 'var(--green-mid)' }}>
              <span>Réduction longue durée ({longStayRule.label || 'séjour long'})</span>
              <span>incluse</span>
            </div>
          )}
        </div>
      )}

      {cleaningFee > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize, padding: '5px 0' }}>
          <span style={{ color: 'var(--ink-3)' }}>Frais de ménage</span>
          <span>{fmtNum(cleaningFee)} €</span>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize, padding: '5px 0' }}>
        <span style={{ color: 'var(--ink-3)' }}>Frais Havenly (8%)</span>
        <span>{fmtNum(havenlyFee)} €</span>
      </div>
      {taxeSejour > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize, padding: '5px 0' }}>
          <span style={{ color: 'var(--ink-3)' }}>Taxe de séjour</span>
          <span>{fmtNum(taxeSejour)} €</span>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize, padding: '5px 0', color: 'var(--green-mid)' }}>
        <span>Assurance annulation</span>
        <span style={{ fontWeight: 500 }}>incluse</span>
      </div>
      {taxeSejour === 0 && <div style={{ fontSize: 11, color: 'var(--ink-4)', paddingTop: 6, borderTop: '0.5px solid var(--border)', marginTop: 4 }}>Taxe de séjour collectée directement par l'hôte.</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, fontSize: fontSize + 2, padding: '10px 0 0', marginTop: 4, borderTop: '0.5px solid var(--border)' }}>
        <span>Total</span><span>{fmtNum(total)} €</span>
      </div>

      {depositAmt > 0 && (
        <div style={{ marginTop: 10, background: 'var(--surface)', borderRadius: 8, padding: '10px 12px', border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: fontSize - 1, marginBottom: 3 }}>
            <span style={{ fontWeight: 500 }}>🔒 Protection Séjour</span>
            <span style={{ fontWeight: 500 }}>{fmtNum(depositAmt)} €</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-4)', lineHeight: 1.5 }}>Autorisation bancaire uniquement — aucun débit. Libérée automatiquement 48h après votre départ.</div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '6px 0', color: 'var(--green-mid)' }}>
        <span>Économie vs Airbnb</span><span style={{ fontWeight: 500 }}>−{fmtNum(airbnbEst)} €</span>
      </div>
    </div>
  );

  const dispoBanner = bookedRanges.length > 0 && nextAvailable ? (
    <div style={{ padding: '8px 16px', fontSize: 12, color: '#C4784A', background: '#FDF0E6', borderBottom: '0.5px solid var(--border)' }}>
      ⚠ Certaines dates sont réservées — prochaine disponibilité : {nextAvailable.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
    </div>
  ) : null;

  const dIn  = fromISO(dateIn);
  const dOut = fromISO(dateOut);

  const colGauche = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <h1 style={{ fontSize: 18, fontWeight: 500 }}>Votre réservation</h1>

      <div style={{ background: 'var(--white)', border: '0.5px solid var(--border)', borderRadius: 14, padding: 16 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div style={{ width: 64, height: 56, borderRadius: 10, background: l.bg || '#E1F5EE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0, overflow: 'hidden' }}>
            {l.photos?.[0] || l.img ? <img src={l.photos?.[0] || l.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (l.emoji || '🏡')}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, color: 'var(--ink-4)', marginBottom: 2 }}>{l.location || l.city}</div>
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{l.title}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>★ {l.rating || '4.9'} · Hôte : {l.host?.full_name || 'Hôte Havenly'}</div>
          </div>
        </div>
      </div>

      <div style={{ background: 'var(--white)', border: '0.5px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '0.5px solid var(--border)' }}>
          <div style={{ padding: '12px 16px', borderRight: '0.5px solid var(--border)' }}>
            <div style={{ fontSize: 10, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 6 }}>Arrivée</div>
            <input type="date" value={dateIn} min={toISO(todayD)} onChange={e => handleDateIn(e.target.value)}
              style={{ fontSize: 14, fontWeight: 500, border: 'none', outline: 'none', background: 'transparent', fontFamily: 'var(--sans)', cursor: 'pointer', width: '100%', color: 'var(--ink)' }} />
          </div>
          <div style={{ padding: '12px 16px' }}>
            <div style={{ fontSize: 10, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 6 }}>Départ</div>
            <input type="date" value={dateOut}
              min={toISO((() => { const d = fromISO(dateIn); d.setDate(d.getDate() + minNights); return d; })())}
              onChange={e => handleDateOut(e.target.value)}
              style={{ fontSize: 14, fontWeight: 500, border: 'none', outline: 'none', background: 'transparent', fontFamily: 'var(--sans)', cursor: 'pointer', width: '100%', color: 'var(--ink)' }} />
          </div>
        </div>
        <div style={{ padding: '8px 16px', fontSize: 12, color: 'var(--green-mid)', background: 'var(--green-light)', borderBottom: '0.5px solid var(--border)' }}>
          {nights} nuit{nights > 1 ? 's' : ''}{minNights > 1 ? ` · minimum ${minNights} nuits` : ''}
          {hasRules && <span style={{ marginLeft: 8, opacity: 0.8 }}>· tarifs dynamiques actifs</span>}
        </div>
        {dispoBanner}
        <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 14 }}>Voyageurs</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => setGuests(g => Math.max(1, g - 1))} style={{ width: 28, height: 28, borderRadius: '50%', border: '0.5px solid rgba(26,26,24,0.18)', background: 'var(--white)', cursor: 'pointer', fontSize: 16 }}>−</button>
            <span style={{ fontWeight: 500, fontSize: 14, minWidth: 16, textAlign: 'center' }}>{guests}</span>
            <button onClick={() => setGuests(g => Math.min(l.guests_max || l.guests || 10, g + 1))} style={{ width: 28, height: 28, borderRadius: '50%', border: '0.5px solid rgba(26,26,24,0.18)', background: 'var(--white)', cursor: 'pointer', fontSize: 16 }}>+</button>
          </div>
        </div>
      </div>

      <div style={{ background: 'var(--white)', border: '0.5px solid var(--border)', borderRadius: 14, padding: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>Ce séjour est protégé par Havenly</div>
        {[
          { title: 'Annulation gratuite', desc: `jusqu'à 14 jours avant — remboursement intégral` },
          { title: 'Paiement sécurisé', desc: "débité uniquement après confirmation de l'hôte" },
          { title: 'Litige résolu en 72h', desc: 'médiateur Havenly disponible' },
        ].map(g => (
          <div key={g.title} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
            <div style={{ width: 22, height: 22, borderRadius: 6, background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--green)', fontSize: 12 }}>✓</div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--ink)', fontWeight: 500 }}>{g.title}</strong> — {g.desc}
            </div>
          </div>
        ))}
      </div>

      {isMobile && (
        <div style={{ background: 'var(--white)', border: '0.5px solid var(--border)', borderRadius: 16, padding: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 500, marginBottom: 4 }}>{fmtNum(total)} €</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 16 }}>Total · {nights} nuit{nights > 1 ? 's' : ''} · {guests} voyageur{guests > 1 ? 's' : ''}</div>
          {recapLignes(13)}
          <div style={{ marginTop: 14 }}>
            {stripeBlock}
            {bouton}
          </div>
        </div>
      )}
    </div>
  );

  const colDroite = (
    <div style={{ position: 'sticky', top: 80 }}>
      <div style={{ background: 'var(--white)', border: '0.5px solid var(--border)', borderRadius: 16, padding: '18px' }}>
        <div style={{ fontSize: 22, fontWeight: 500, marginBottom: 4 }}>{fmtNum(total)} €</div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16 }}>Total · {nights} nuit{nights > 1 ? 's' : ''} · {guests} voyageur{guests > 1 ? 's' : ''}</div>
        {recapLignes(14)}
        <div style={{ marginTop: 14 }}>
          {stripeBlock}
          {bouton}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh', paddingTop: 60 }}>
      <div style={{ maxWidth: 960, margin: '0 auto', padding: isMobile ? '1rem' : '2rem', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 340px', gap: isMobile ? 16 : 24, alignItems: 'start' }}>
        {colGauche}
        {!isMobile && colDroite}
      </div>
    </div>
  );
}
